import React, { useMemo } from 'react';
import PropTypes, { any } from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import {
    addOpacityToHexColor,
    getMatchTimeDisplay,
    getRoundName,
    hasPositionWon,
    hasSchedularStatus,
    matchSchedulerStatuses,
} from '../../../utils/courtSchedular';
import { restartPausedMatch, startDraggingOfMatch, toggleMatchSelection } from '../../../actions/court-scheduler-actions';
import { getLabelByTypeId } from '../../../utils/types';
import { getMatchOutcomeLabel, getScoresForPosition } from '../../../utils/matchCardUtils';
import { PositionNumbers } from '../../../utils/constants';

import styles from './courtSchedular.scss';
import { CheckBox } from '../../../components/Form';
import Img from '../../../components/Img';
import InlineScores from './InlineScores';
import MatchPlayerOrTeamDetails from './MatchPlayerOrTeamDetails';

export const getResultTagLabel = (match) => {
    if (match.schedulerStatus === matchSchedulerStatuses.IN_PROGRESS) {
        return 'In Progress';
    }

    if (match.schedulerStatus === matchSchedulerStatuses.PAUSED) {
        return 'Paused';
    }

    if (match.schedulerStatus === matchSchedulerStatuses.COMPLETED) {
        const outcomeLabel = getMatchOutcomeLabel(match.result);
        return outcomeLabel || 'Completed';
    }

    return null;
};

const Match = (props) => {
    const {
        match,
        isScheduled = false,
    } = props;

    const dispatch = useDispatch();
    const coreUI = useSelector(({ coreUI }) => coreUI);
    const drawEdit = useSelector(({ drawEdit }) => drawEdit);
    const courtScheduler = useSelector(({ courtScheduler }) => courtScheduler);
    const {
        draggableMatch,
        selectedMatchIds = [],
    } = courtScheduler;

    const drawOfficials = drawEdit.officials?.data || [];
    const eventSchedule = drawEdit.event?.data?.eventSchedule;
    const matchOfficial = drawOfficials.find((official) => {
        return official.id === match.officialId;
    });

    const {
        division: {
            tournament: {
                scoringTypes,
            },
        },
    } = coreUI.types.tms;

    const isMatchBeingDragged = !isScheduled && draggableMatch?.id === match.id;

    const customStyles = {
        border: `2px solid ${addOpacityToHexColor(match.drawColor, 0.4)}`,
        backgroundColor: addOpacityToHexColor(match.drawColor, 0.1),
        ...(isMatchBeingDragged && ({
            opacity: 0.5,
        })),
    };

    const handleDragStart = () => {
        if (!isScheduled) {
            dispatch(startDraggingOfMatch(match));
        }
    };

    const handleDragEnd = () => {
        if (!isScheduled) {
            dispatch(startDraggingOfMatch(null));
        }
    };

    const scoringType = getLabelByTypeId(scoringTypes, match.scoringTypeId);

    const hasResult = match?.result;

    const hasPosition1WonGame = hasResult && hasPositionWon('position1', match);
    const hasPosition2WonGame = hasResult && hasPositionWon('position2', match);

    const position1Scores = useMemo(() => {
        if (!hasResult) {
            return null;
        }
        return getScoresForPosition(match, PositionNumbers.POSITION_1);
    }, [match, hasResult]);

    const position2Scores = useMemo(() => {
        if (!hasResult) {
            return null;
        }
        return getScoresForPosition(match, PositionNumbers.POSITION_2);
    }, [match, hasResult]);

    const isMatchSelected = selectedMatchIds.includes(match.id);

    const hasMatchStatus = hasSchedularStatus(match);
    const isMatchPaused = hasMatchStatus && match.schedulerStatus === matchSchedulerStatuses.PAUSED;

    const resultTag = getResultTagLabel(match);
    const resultTagStyles = {
        backgroundColor: match.schedulerStatus === matchSchedulerStatuses.COMPLETED ? 'black' : match.drawColor,
    };

    const onCheckChange = (checked, ev) => {
        ev.preventDefault();
        ev.stopPropagation();
        dispatch(toggleMatchSelection(match.id));
    };

    const onRestartClick = (e) => {
        e.stopPropagation();
        dispatch(restartPausedMatch(match.id));
    };

    return (
        <div
            className={`${styles.draggableMatch} mt4 mb4 ${match.addDisableOverlay ? styles.disable : ''}`}
            style={customStyles}
            draggable={!isScheduled}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            data-testid={`court-scheduler.match-${match.id}`}
        >
            {resultTag && (
                <div className={styles.statusLayout}>
                    <div className={styles.matchStatus} style={resultTagStyles} data-testid={`court-scheduler.match-${match.id}.status`}>
                        {resultTag}
                    </div>
                    {isMatchPaused && (
                        <button
                            className={`btn btn-flex p0 ${styles.play}`}
                            type="button"
                            onClick={onRestartClick}
                            data-testid={`court-scheduler.match-${match.id}.play-btn`}
                        >
                            Play
                        </button>
                    )}
                </div>
            )}
            {match.isPrinted &&
                (
                    <div className={styles.printIcon} style={resultTagStyles}>
                        <Img src="/img/v3/icons/icon-print-white.svg" />
                    </div>
                )}
            <div className={`${styles.matchData}`}>
                {isScheduled && (
                    <div className={styles.selectContainer}>
                        <div className="flex-1" />
                        {isScheduled && (
                            <CheckBox
                                id={`select-${match.id}`}
                                formGroupCssClass={`${styles.matchSelect} ${isMatchSelected && styles.selected}`}
                                controlled={true}
                                checked={isMatchSelected}
                                onChange={onCheckChange}
                                disabled={false}
                            />
                        )}
                    </div>
                )}
                {isScheduled && (
                    <div className={`${styles.timeDetails}`} data-testid={`court-scheduler.match-${match.id}.time`}>
                        {getMatchTimeDisplay(match, eventSchedule)}
                    </div>
                )}
                <div className={styles.positionContent}>
                    <MatchPlayerOrTeamDetails
                        className={`${styles.player} ${hasPosition1WonGame ? styles.wonMatch : ''}`}
                        positionParam="position1"
                        match={match}
                    />
                    <InlineScores scores={position1Scores} />
                    {hasPosition1WonGame && <div className={styles.winnerArrow} />}
                </div>
                <div className={styles.playerSeparator} />
                <div className={styles.positionContent}>
                    <MatchPlayerOrTeamDetails
                        className={`${styles.player} ${hasPosition2WonGame ? styles.wonMatch : ''}`}
                        positionParam="position2"
                        match={match}
                    />
                    <InlineScores scores={position2Scores} />
                    {hasPosition2WonGame && <div className={styles.winnerArrow} />}
                </div>
                {isScheduled && <div className="mb4" />}
                {isScheduled && (
                    <div className={`${styles.details}`} data-testid={`court-scheduler.match-${match.id}.parent-draw`}>
                        {match.parentDrawName}
                    </div>
                )}
                <div className={`${styles.details}`} data-testid={`court-scheduler.match-${match.id}.draw-round`}>
                    {`${match.drawName}, ${getRoundName(match)}`}
                </div>
                {scoringType && (
                    <div className={`${styles.details}`} data-testid={`court-scheduler.match-${match.id}.scoring-type`}>
                        {scoringType}
                    </div>
                )}
                {isScheduled && matchOfficial && (
                    <div className={`${styles.details}`} data-testid={`court-scheduler.match-${match.id}.official`}>
                        {`R: ${matchOfficial?.firstName} ${matchOfficial?.lastName}`}
                    </div>
                )}
            </div>
            {isScheduled && match.addDisableOverlay && (
                <div className={styles.disableOverlay} />
            )}
        </div>
    );
};

Match.propTypes = {
    match: PropTypes.objectOf(any).isRequired,
    isScheduled: PropTypes.bool,
};

Match.defaultProps = {
    isScheduled: false,
};

export default Match;
